import React from 'react';

import { DATE_FIELD_PASSTHROUGH_KEYS } from './date-mask-helper.const';
import { DateTimeFormat, DayjsFormatMapper } from './date-time.const';

/**
 * Returns `true` if a keydown event should be allowed through in a date input field.
 *
 * Permits:
 * - Keyboard shortcuts (Ctrl/Cmd combos, e.g. copy/paste/select-all)
 * - Navigation and editing keys defined in `DATE_FIELD_PASSTHROUGH_KEYS`
 * - The `/` separator and digit characters (`0`–`9`)
 *
 * All other keys are blocked so non-date characters can never be typed.
 */
export function allowedDateFieldEntry(
  event: React.KeyboardEvent<HTMLInputElement>,
): boolean {
  if (event.ctrlKey || event.metaKey) return true;
  if (DATE_FIELD_PASSTHROUGH_KEYS.has(event.key)) return true;
  if (event.key === '/' || /^\d$/.test(event.key)) return true;
  return false;
}

/**
 * Applies a date input mask (XX/XX/XXXX) to raw input, auto-inserting separators
 * and auto-prepending "0" when a digit unambiguously requires a leading zero.
 *
 * Auto-zero rules (applied only when typing forward, not deleting):
 * - Day slot (max 31): first digit > 3 → prepend "0" (e.g. "4" → "04/")
 * - Month slot (max 12): first digit > 1 → prepend "0" (e.g. "2" → "02/")
 *
 * Which slot is day vs month is determined by `format`:
 * - DD/MM/YYYY (Default): slot 1 = day, slot 2 = month
 * - MM/DD/YYYY (DefaultUSA): slot 1 = month, slot 2 = day
 *
 * Mid-string deletion: when exactly one digit is removed from a value that already
 * contains "/" separators, each slot is reformatted independently so adjacent slots
 * (e.g. the year) are never shifted.
 */
export function applyDateMask(
  value: string,
  prevValue: string,
  format: DayjsFormatMapper = DateTimeFormat.Default,
): string {
  const isDeleting = value.length < prevValue.length;
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (isDeleting) {
    const deletionResult = handleDeletionMask(value, prevValue, digits);
    if (deletionResult !== null) return deletionResult;
  }
  const slot1IsDay = format !== DateTimeFormat.DefaultUSA;
  const slot1Threshold = slot1IsDay ? 3 : 1;
  const slot2Threshold = slot1IsDay ? 1 : 3;
  // Detect when the user manually typed "/" (value ends with "/" but prevValue didn't).
  // Used to auto-zero a single-digit slot so the separator is accepted.
  const userTypedSeparator = value.endsWith('/') && !prevValue.endsWith('/');

  if (!isDeleting) {
    const recovered = tryRecoverEmptySlot(
      value,
      prevValue,
      slot1Threshold,
      slot2Threshold,
      userTypedSeparator,
    );
    if (recovered !== null) return recovered;
  }
  const processedDigits = isDeleting
    ? digits
    : applyAutoZero(digits, slot1Threshold, slot2Threshold, userTypedSeparator);
  return formatMaskedDigits(processedDigits, isDeleting);
}

/**
 * Handles the deletion path of the date mask.
 *
 * For a single-digit mid-string deletion (cursor was between separators), preserves
 * the slot structure via `rebuildFromSlots` so adjacent slots are not shifted.
 * Returns `null` for "/" deletions or range deletions, signalling the caller to fall
 * through to the flat digit-strip rebuild which restores any removed separators.
 */
function handleDeletionMask(
  value: string,
  prevValue: string,
  digits: string,
): string | null {
  const prevDigitCount = prevValue.replace(/\D/g, '').length;
  const isSingleDigitDeleted = prevDigitCount - digits.length === 1;
  if (isSingleDigitDeleted && value.includes('/')) return rebuildFromSlots(value);
  return null;
}

/**
 * Formats a raw digit string into a masked date (XX/XX/XXXX), capping at 8 digits
 * and auto-appending a trailing "/" after the day and month slots when typing forward.
 */
function formatMaskedDigits(digits: string, isDeleting: boolean): string {
  const capped = digits.substring(0, 8);
  let formatted = capped.substring(0, 2);
  if (capped.length > 2) formatted += '/' + capped.substring(2, 4);
  if (capped.length > 4) formatted += '/' + capped.substring(4, 8);
  if (!isDeleting && (capped.length === 2 || capped.length === 4)) formatted += '/';
  return formatted;
}

/**
 * Handles re-entry into a fully emptied slot after the last digit was deleted.
 *
 * When a slot is emptied mid-string, the flat digit-strip rebuild would redistribute
 * all remaining digits incorrectly. This function detects the two emptied-slot states
 * and grafts the new digit back onto the preserved surrounding slots instead.
 *
 * - Slot 1 empty: `prevValue` starts with `/` (e.g. `/09/2022`)
 * - Slot 2 empty: `prevValue` contains `//` (e.g. `10//2022`)
 *
 * Returns the rebuilt string, or `null` if neither recovery path applies.
 */
function tryRecoverEmptySlot(
  value: string,
  prevValue: string,
  slot1Threshold: number,
  slot2Threshold: number,
  userTypedSeparator: boolean,
): string | null {
  if (!value.includes('/')) return null;

  if (prevValue.startsWith('/')) {
    const newSlot1Raw = value.substring(0, value.indexOf('/')).replace(/\D/g, '');
    if (newSlot1Raw.length > 2) return null;
    const autoZeroed = applyAutoZero(
      newSlot1Raw,
      slot1Threshold,
      slot2Threshold,
      userTypedSeparator,
    );
    return autoZeroed + prevValue;
  }

  if (prevValue.includes('//')) {
    const [prevSlot1 = '', , prevSlot3 = ''] = prevValue.split('/');
    const newSlot2Raw = (value.split('/')[1] ?? '').replace(/\D/g, '').substring(0, 2);
    if (newSlot2Raw.length > 2) return null;
    const needsAutoZero =
      newSlot2Raw.length === 1 &&
      (parseInt(newSlot2Raw[0]) > slot2Threshold || userTypedSeparator);
    const autoZeroed = needsAutoZero ? '0' + newSlot2Raw : newSlot2Raw;
    return prevSlot1 + '/' + autoZeroed + (prevSlot3 ? '/' + prevSlot3 : '');
  }

  return null;
}

/**
 * Prepends "0" to a slot's first digit when it unambiguously requires a leading zero.
 *
 * Triggers when:
 * - The digit in slot 1 (1 digit total) exceeds `slot1Threshold`, or
 * - The digit in slot 2 (3 digits total) exceeds `slot2Threshold`, or
 * - The user manually typed a "/" separator after a single-digit slot.
 */
function applyAutoZero(
  digits: string,
  slot1Threshold: number,
  slot2Threshold: number,
  userTypedSeparator: boolean,
): string {
  if (
    digits.length === 1 &&
    (parseInt(digits[0]) > slot1Threshold || userTypedSeparator)
  ) {
    return '0' + digits;
  }
  if (
    digits.length === 3 &&
    (parseInt(digits[2]) > slot2Threshold || userTypedSeparator)
  ) {
    return digits.substring(0, 2) + '0' + digits[2];
  }
  return digits;
}

/**
 * Rebuilds a date string by processing each "/"-separated slot independently.
 * Only digits are kept per slot; slot lengths are capped at 2/2/4.
 * Used for mid-string deletions to prevent adjacent slots from shifting.
 */
function rebuildFromSlots(value: string): string {
  const [s1 = '', s2 = '', s3 = ''] = value.split('/');
  const slot1 = s1.replace(/\D/g, '').substring(0, 2);
  const slot2 = s2.replace(/\D/g, '').substring(0, 2);
  const slot3 = s3.replace(/\D/g, '').substring(0, 4);

  if (!slot1 && !slot2 && !slot3) return '';

  const segments = value.split('/');
  let result = slot1;
  if (segments.length >= 2) result += '/' + slot2;
  if (segments.length >= 3) result += '/' + slot3;
  return result;
}
